/**
 * Responsive Image Container Component
 * Handles responsive scaling of plant images with proper overlay positioning
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';

// Types
interface ResponsiveImageContainerProps {
  src: string;
  alt: string;
  originalWidth: number;
  originalHeight: number;
  children?: React.ReactNode; // Overlays and other content
  onImageLoad?: (dimensions: { width: number; height: number; scale: number }) => void;
  onImageError?: () => void;
  maxWidth?: string | number;
  maxHeight?: string | number;
  maintainAspectRatio?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

interface ImageDimensions {
  width: number;
  height: number;
  scale: number;
  offsetX: number;
  offsetY: number;
}

export const ResponsiveImageContainer: React.FC<ResponsiveImageContainerProps> = ({
  src,
  alt,
  originalWidth,
  originalHeight,
  children,
  onImageLoad,
  onImageError,
  maxWidth = '100%',
  maxHeight = '60vh',
  maintainAspectRatio = true,
  className,
  style,
}) => {
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions>({
    width: 0,
    height: 0,
    scale: 1,
    offsetX: 0,
    offsetY: 0,
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Calculate image dimensions and scale
  const calculateDimensions = useCallback(() => {
    if (!containerRef.current || !imageRef.current || !imageLoaded) {
      return;
    }

    const container = containerRef.current;
    const image = imageRef.current;
    
    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    // Calculate scale factors
    const scaleX = containerWidth / originalWidth;
    const scaleY = containerHeight / originalHeight;
    
    let scale: number;
    let displayWidth: number;
    let displayHeight: number;

    if (maintainAspectRatio) {
      // Use the smaller scale to maintain aspect ratio
      scale = Math.min(scaleX, scaleY);
      displayWidth = originalWidth * scale;
      displayHeight = originalHeight * scale;
    } else {
      // Stretch to fill container
      scale = Math.min(scaleX, scaleY); // Still calculate based on smaller scale for overlays
      displayWidth = containerWidth;
      displayHeight = containerHeight;
    }

    // Calculate centering offsets
    const offsetX = (containerWidth - displayWidth) / 2;
    const offsetY = (containerHeight - displayHeight) / 2;

    const dimensions: ImageDimensions = {
      width: displayWidth,
      height: displayHeight,
      scale,
      offsetX,
      offsetY,
    };

    // Only update if dimensions have actually changed (prevent unnecessary re-renders)
    setImageDimensions(prev => {
      const threshold = 1; // 1px threshold for changes
      if (Math.abs(prev.width - dimensions.width) < threshold &&
          Math.abs(prev.height - dimensions.height) < threshold &&
          Math.abs(prev.scale - dimensions.scale) < 0.01 &&
          Math.abs(prev.offsetX - dimensions.offsetX) < threshold &&
          Math.abs(prev.offsetY - dimensions.offsetY) < threshold) {
        return prev; // No meaningful change, keep previous state
      }
      return dimensions;
    });
    
    // Apply dimensions to image (let flex container handle centering)
    if (imageRef.current) {
      imageRef.current.style.width = `${displayWidth}px`;
      imageRef.current.style.height = `${displayHeight}px`;
      // Remove explicit margins - let flex container center the image
      imageRef.current.style.marginLeft = '0';
      imageRef.current.style.marginTop = '0';
    }

    // Notify parent of dimensions
    onImageLoad?.(dimensions);
  }, [originalWidth, originalHeight, maintainAspectRatio, imageLoaded, onImageLoad]);

  // Handle image load
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
  }, []);

  // Handle image error
  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(false);
    onImageError?.();
  }, [onImageError]);

  // Recalculate dimensions when image loads or container resizes
  useEffect(() => {
    calculateDimensions();
  }, [calculateDimensions]);

  // Add resize observer for responsive behavior with proper debouncing
  useEffect(() => {
    if (!containerRef.current) return;

    let timeoutId: NodeJS.Timeout;
    let lastSize = { width: 0, height: 0 };
    
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      
      const { width, height } = entry.contentRect;
      
      // Only trigger if size actually changed significantly (avoid micro-adjustments)
      const sizeThreshold = 10; // 10px threshold
      if (Math.abs(width - lastSize.width) < sizeThreshold && 
          Math.abs(height - lastSize.height) < sizeThreshold) {
        return;
      }
      
      lastSize = { width, height };
      
      // Clear previous timeout and set new one for debouncing
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        calculateDimensions();
      }, 300); // Increased to 300ms for more stability
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [calculateDimensions]);

  // Get responsive container styles
  const getContainerStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      backgroundColor: '#ffffff',
      border: '2px solid #ddd',
      borderRadius: '8px',
      minHeight: '200px',
      pointerEvents: 'auto', // Ensure container allows clicks to children
      // CRITICAL: Prevent container from growing in ANY direction
      flexShrink: 0, // Don't allow shrinking
      flexGrow: 0,   // Don't allow growing
      boxSizing: 'border-box', // Include borders in size calculations
      ...style,
    };

    // Set FIXED height based on screen size to prevent growth
    if (isMobile) {
      baseStyles.width = '100%';
      baseStyles.height = '50vh'; // Fixed height, not max
      baseStyles.maxWidth = '100%';
      baseStyles.maxHeight = '50vh'; // Enforce maximum
    } else if (isTablet) {
      baseStyles.width = typeof maxWidth === 'string' ? maxWidth : `${maxWidth}px`;
      baseStyles.height = '55vh'; // Fixed height, not max
      baseStyles.maxWidth = maxWidth;
      baseStyles.maxHeight = '55vh'; // Enforce maximum
    } else {
      baseStyles.width = typeof maxWidth === 'string' ? maxWidth : `${maxWidth}px`;
      baseStyles.height = typeof maxHeight === 'string' ? maxHeight : `${maxHeight}px`; // Fixed height
      baseStyles.maxWidth = maxWidth;
      baseStyles.maxHeight = maxHeight; // Enforce maximum
    }

    return baseStyles;
  };

  // Get image styles
  const getImageStyles = (): React.CSSProperties => ({
    display: imageLoaded ? 'block' : 'none',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: maintainAspectRatio ? 'contain' : 'fill',
    transition: 'all 0.3s ease-in-out',
  });

  // Get overlay container styles (positioned over flex-centered image)
  const getOverlayContainerStyles = (): React.CSSProperties => ({
    position: 'absolute',
    // Since image is now flex-centered, overlays should be positioned from (0,0) 
    // to align with the centered image position
    top: imageDimensions.offsetY,
    left: imageDimensions.offsetX,
    width: imageDimensions.width,
    height: imageDimensions.height,
    pointerEvents: 'none', // Allow interactions to pass through
    // Ensure overlays align with flex-centered image
    transform: 'translate(0, 0)', // Reset any inherited transforms
  });

  return (
    <Box
      ref={containerRef}
      className={className}
      sx={{
        ...getContainerStyles(),
        // Enforce strict size constraints with CSS
        '&': {
          minHeight: '200px !important',
          maxHeight: isMobile ? '50vh !important' : isTablet ? '55vh !important' : (typeof maxHeight === 'string' ? `${maxHeight} !important` : `${maxHeight}px !important`),
          overflow: 'hidden !important',
          position: 'relative !important',
          flexShrink: '0 !important',
          flexGrow: '0 !important',
        }
      }}
    >
      {/* Loading placeholder */}
      {!imageLoaded && !imageError && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            typography: 'body2',
          }}
        >
          Loading image...
        </Box>
      )}

      {/* Error placeholder */}
      {imageError && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'error.main',
            typography: 'body2',
            p: 2,
          }}
        >
          <Box sx={{ mb: 1 }}>⚠️</Box>
          <Box>Failed to load image</Box>
        </Box>
      )}

      {/* Main image */}
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        style={getImageStyles()}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />

      {/* Overlay container */}
      {imageLoaded && children && (
        <Box style={getOverlayContainerStyles()}>
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              // Pass scale and dimension information to overlay children
              return React.cloneElement(child, {
                ...child.props,
                imageWidth: originalWidth,
                imageHeight: originalHeight,
                containerWidth: imageDimensions.width,
                containerHeight: imageDimensions.height,
                scale: imageDimensions.scale,
              } as any);
            }
            return child;
          })}
        </Box>
      )}
    </Box>
  );
};

// Hook for getting responsive breakpoint information
export const useResponsiveBreakpoint = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  return {
    isMobile,
    isTablet,
    isDesktop,
    breakpoint: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
  };
};

// Hook for managing responsive image dimensions
export const useResponsiveImageDimensions = (
  originalWidth: number,
  originalHeight: number,
  containerWidth: number,
  containerHeight: number,
  maintainAspectRatio = true
) => {
  return React.useMemo(() => {
    if (!containerWidth || !containerHeight) {
      return {
        width: 0,
        height: 0,
        scale: 1,
        offsetX: 0,
        offsetY: 0,
      };
    }

    const scaleX = containerWidth / originalWidth;
    const scaleY = containerHeight / originalHeight;
    
    let scale: number;
    let displayWidth: number;
    let displayHeight: number;

    if (maintainAspectRatio) {
      scale = Math.min(scaleX, scaleY);
      displayWidth = originalWidth * scale;
      displayHeight = originalHeight * scale;
    } else {
      scale = Math.min(scaleX, scaleY);
      displayWidth = containerWidth;
      displayHeight = containerHeight;
    }

    const offsetX = (containerWidth - displayWidth) / 2;
    const offsetY = (containerHeight - displayHeight) / 2;

    return {
      width: displayWidth,
      height: displayHeight,
      scale,
      offsetX,
      offsetY,
    };
  }, [originalWidth, originalHeight, containerWidth, containerHeight, maintainAspectRatio]);
};

export default ResponsiveImageContainer;

